import { canonicalManager } from '@/utils/canonicalManager'
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'

/**
 * SEO 中間件 - 處理重複內容和索引問題
 * 增強版：防止雙重語言代碼和無效路徑
 */
export function seoMiddleware(
  to: RouteLocationNormalized, 
  _from: RouteLocationNormalized, 
  next: NavigationGuardNext
) {
  const locale = to.params.locale as string
  const supportedLocales = ['zh-TW', 'zh-CN', 'en', 'hi', 'ja', 'ko']
  
  // 檢查並防止雙重語言代碼
  const pathSegments = to.path.split('/').filter(segment => segment)
  const duplicateLocaleCheck = pathSegments.filter(segment => 
    supportedLocales.includes(segment)
  )
  
  if (duplicateLocaleCheck.length > 1) {
    // 檢測到雙重語言代碼，重定向到第一個有效的語言代碼
    const validLocale = duplicateLocaleCheck[0]
    const remainingPath = to.path
      .replace(new RegExp(`/(${supportedLocales.join('|')})/`, 'g'), '/')
      .replace(/\/+/g, '/')
    const redirectPath = `/${validLocale}${remainingPath === '/' ? '' : remainingPath}`
    
    console.warn(`檢測到雙重語言代碼: ${to.path} -> ${redirectPath}`)
    return next(redirectPath)
  }
  
  // 檢查語言有效性
  if (!locale || !supportedLocales.includes(locale)) {
    const defaultLocale = 'zh-TW'
    const pathWithoutLocale = to.path.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, '') || '/'
    const redirectPath = `/${defaultLocale}${pathWithoutLocale}`
    
    console.warn(`無效語言代碼: ${locale} -> ${redirectPath}`)
    return next(redirectPath)
  }

  // 檢查無效的路徑組合
  const invalidPatterns = [
    /\/examples\//,
    /\/tutorials\//,
    /\/guides\//,
    /\/blog\//,
    /\/api\/getting-started/,
    /\/tools\/new$/,
    /\/tools\/category\//,
    /\/tools\/popular$/
  ]
  
  for (const pattern of invalidPatterns) {
    if (pattern.test(to.path)) {
      const redirectPath = `/${locale}/tools`
      console.warn(`無效路徑: ${to.path} -> ${redirectPath}`)
      return next(redirectPath)
    }
  }

  // 檢查是否為重複內容
  if (canonicalManager.isDuplicateContent(to.fullPath, locale)) {
    const canonicalPath = canonicalManager.handleDuplicateContent(to.fullPath, locale)
    if (canonicalPath) {
      console.log(`重複內容重定向: ${to.fullPath} -> ${canonicalPath}`)
      return next(canonicalPath)
    }
  }

  // 設置 canonical URL 和 hreflang
  setTimeout(() => {
    try {
      canonicalManager.setCanonicalURL(to.fullPath, locale)
      canonicalManager.setHreflangTags(to.fullPath)
    } catch (error) {
      console.error('設置 SEO 標籤時發生錯誤:', error)
    }
  }, 0)

  next()
}

/**
 * 處理 URL 正規化 - 增強版
 */
export function normalizeURL(path: string): string {
  // 移除重複的斜線
  let normalized = path.replace(/\/+/g, '/')
  
  // 移除尾部斜線（除了根路徑和語言根路徑）
  if (normalized.length > 1 && normalized.endsWith('/')) {
    // 檢查是否為語言根路徑 (如 /zh-TW/, /en/)
    const isLanguageRoot = /^\/[a-z]{2}(-[A-Z]{2})?\/$/i.test(normalized)
    if (!isLanguageRoot) {
      normalized = normalized.slice(0, -1)
    }
  }
  
  // 確保根路徑有尾部斜線
  if (normalized === '') {
    normalized = '/'
  }
  
  return normalized
}

/**
 * 檢查是否需要重新導向 - 增強版
 */
export function checkRedirectNeeded(to: RouteLocationNormalized): string | null {
  const currentPath = to.fullPath
  const normalizedPath = normalizeURL(currentPath)
  
  // 如果路徑不一致，需要重新導向
  if (currentPath !== normalizedPath) {
    return normalizedPath
  }
  
  return null
}

/**
 * 驗證路由是否存在 - 新增
 */
export function validateRouteExists(path: string): boolean {
  // 定義實際存在的路由模式
  const validRoutePatterns = [
    /^\/[a-z]{2}(-[A-Z]{2})?\/$/,                    // 語言根路徑
    /^\/[a-z]{2}(-[A-Z]{2})?\/tools$/,              // 工具總覽
    /^\/[a-z]{2}(-[A-Z]{2})?\/tools\/[a-z-]+$/,     // 具體工具
    /^\/[a-z]{2}(-[A-Z]{2})?\/about$/,              // 關於頁面
    /^\/[a-z]{2}(-[A-Z]{2})?\/privacy$/,            // 隱私政策
    /^\/[a-z]{2}(-[A-Z]{2})?\/terms$/,              // 服務條款
    /^\/[a-z]{2}(-[A-Z]{2})?\/ranking$/             // 工具排行榜
  ]
  
  return validRoutePatterns.some(pattern => pattern.test(path))
}

/**
 * 清理和驗證 URL - 新增
 */
export function sanitizeAndValidateURL(url: string): string | null {
  try {
    // 移除開發環境端口
    url = url.replace(/:8443/g, '')
    
    // 正規化 URL
    url = normalizeURL(url)
    
    // 驗證是否為有效路由
    if (validateRouteExists(url)) {
      return url
    }
    
    return null
  } catch (error) {
    console.error('URL 清理和驗證失敗:', error)
    return null
  }
}

/**
 * 阻止開發環境 URL 洩漏 - 新增
 */
export function preventDevUrlLeak(): void {
  // 在生產環境中，確保不會有開發端口的 URL
  if (typeof window !== 'undefined' && window.location.href.includes(':8443')) {
    const cleanUrl = window.location.href.replace(':8443', '')
    window.location.replace(cleanUrl)
  }
} 